import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';
import cytoscape, { Core, NodeSingular, ElementDefinition } from 'cytoscape';
import fcose from 'cytoscape-fcose';
import { Campaign } from '../../models/campaign';
import { Company } from '../../models/company';
import { Roster } from '../../models/roster';
import { CampaignService } from '../../services/campaign.service';
import { CompanyService } from '../../services/company.service';
import { RosterService } from '../../services/roster.service';

cytoscape.use(fcose);

type NodeKind = 'creator' | 'agency' | 'brand' | 'company';

interface NodeCampaignRow {
  id: string;
  name: string;
  rate: number;
  creatorId?: string;
}

interface SelectedNodeInfo {
  id: string;
  label: string;
  kind: NodeKind;
  kindLabel: string;
  color: string;
  totalRate: number;
  campaigns: NodeCampaignRow[];
}

const KIND_COLORS: Record<NodeKind, string> = {
  creator: '#4caf50',
  agency: '#2196f3',
  brand: '#f44336',
  company: '#9e9e9e'
};

const KIND_LABELS: Record<NodeKind, string> = {
  creator: 'Creator',
  agency: 'Agency',
  brand: 'Brand',
  company: 'Company'
};

const MIN_NODE_SIZE = 24;
const MAX_NODE_SIZE = 110;

function formatRate(rate: number): string {
  if (rate >= 1_000_000) {
    return '$' + parseFloat((rate / 1_000_000).toFixed(1)) + 'M';
  }
  if (rate >= 1_000) {
    return '$' + parseFloat((rate / 1_000).toFixed(1)) + 'k';
  }
  return '$' + Math.round(rate);
}

// Layout state persisted across route navigations (module-scoped so it
// survives component destroy/recreate but not a full page refresh).
interface SavedGraphState {
  positions: Record<string, { x: number; y: number }>;
  zoom: number;
  pan: { x: number; y: number };
  showUnconnected: boolean;
}
let savedGraphState: SavedGraphState | null = null;

@Component({
  selector: 'app-crm-graph',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './crm-graph.component.html',
  styleUrl: './crm-graph.component.css'
})
export class CrmGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('graphContainer') graphContainer!: ElementRef<HTMLDivElement>;

  loading = true;
  error = '';
  searchTerm = '';
  searchError = '';
  showUnconnected = false;

  selectedNode: SelectedNodeInfo | null = null;

  nodeCount = 0;
  edgeCount = 0;

  private cy?: Core;
  private connectedElements: ElementDefinition[] = [];
  private unconnectedElements: ElementDefinition[] = [];
  private firstLayoutDone = false;
  private rateScopeCreatorId: string | null = null;
  private dataReady = false;
  private viewReady = false;
  private fitTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private campaignService: CampaignService,
    private companyService: CompanyService,
    private rosterService: RosterService,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    // Fetch in ngOnInit: the services return cached data synchronously on
    // revisits, and mutating `loading` after the view was checked (as in
    // ngAfterViewInit) triggers NG0100.
    this.showUnconnected = savedGraphState?.showUnconnected ?? false;
    forkJoin({
      campaigns: this.campaignService.getCampaigns(),
      companies: this.companyService.getCompanies(),
      rosters: this.rosterService.getRosters()
    }).subscribe({
      next: ({ campaigns, companies, rosters }) => {
        this.buildElements(campaigns, companies, rosters);
        this.loading = false;
        this.dataReady = true;
        if (this.viewReady) {
          this.initGraph();
        }
      },
      error: (err) => {
        console.error('Failed to load CRM graph data', err);
        this.error = 'Failed to load graph data. Please try again.';
        this.loading = false;
      }
    });
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    if (this.dataReady) {
      this.initGraph();
    }
  }

  ngOnDestroy(): void {
    if (this.fitTimer) {
      clearTimeout(this.fitTimer);
      this.fitTimer = undefined;
    }
    if (this.cy) {
      const positions: Record<string, { x: number; y: number }> = {};
      this.cy.nodes().forEach(n => { positions[n.id()] = { ...n.position() }; });
      savedGraphState = {
        positions,
        zoom: this.cy.zoom(),
        pan: { ...this.cy.pan() },
        showUnconnected: this.showUnconnected
      };
      this.cy.destroy();
    }
  }

  // ── Graph construction ─────────────────────────────────────────

  private buildElements(campaigns: Campaign[], companies: Company[], rosters: Roster[]): void {
    const activeCampaigns = campaigns.filter(c => !c.deleted);

    // Per-node aggregates: total rate + campaign list
    const totals = new Map<string, number>();
    const nodeCampaigns = new Map<string, NodeCampaignRow[]>();
    const addRate = (nodeId: string | undefined, campaign: Campaign) => {
      if (!nodeId) { return; }
      totals.set(nodeId, (totals.get(nodeId) ?? 0) + (campaign.rate || 0));
      const rows = nodeCampaigns.get(nodeId) ?? [];
      rows.push({
        id: campaign.id,
        name: campaign.name || '(unnamed campaign)',
        rate: campaign.rate || 0,
        creatorId: campaign.creatorId
      });
      nodeCampaigns.set(nodeId, rows);
    };
    for (const campaign of activeCampaigns) {
      addRate(campaign.creatorId, campaign);
      addRate(campaign.accountId, campaign);
      // Same company as both account and brand: count the campaign once.
      if (campaign.brandId !== campaign.accountId) {
        addRate(campaign.brandId, campaign);
      }
    }

    for (const rows of nodeCampaigns.values()) {
      rows.sort((a, b) => b.rate - a.rate);
    }

    const maxRate = Math.max(1, ...Array.from(totals.values()));
    const sizeFor = (total: number) => {
      const scaled = MIN_NODE_SIZE + (MAX_NODE_SIZE - MIN_NODE_SIZE) * Math.sqrt(total / maxRate);
      return Math.min(MAX_NODE_SIZE, Math.max(MIN_NODE_SIZE, scaled));
    };

    const nodeIds = new Set<string>();
    const nodes: ElementDefinition[] = [];
    const addNode = (id: string, label: string, kind: NodeKind) => {
      if (!id || nodeIds.has(id)) { return; }
      nodeIds.add(id);
      const totalRate = totals.get(id) ?? 0;
      const size = sizeFor(totalRate);
      const name = label || '(unnamed)';
      nodes.push({
        group: 'nodes',
        data: {
          id,
          label: name,
          displayLabel: totalRate > 0 ? `${name}\n${formatRate(totalRate)}` : name,
          kind,
          kindLabel: KIND_LABELS[kind],
          color: KIND_COLORS[kind],
          size,
          fontSize: Math.max(9, Math.round(size / 5)),
          totalRate,
          campaigns: nodeCampaigns.get(id) ?? []
        }
      });
    };

    for (const roster of rosters) {
      addNode(roster.id, roster.name, 'creator');
    }
    for (const company of companies) {
      const kind: NodeKind = company.type === 'Agency' ? 'agency'
        : company.type === 'Brand' ? 'brand'
        : 'company';
      addNode(company.id, company.name, kind);
    }

    const maxCampaignRate = Math.max(1, ...activeCampaigns.map(c => c.rate || 0));
    const edgeWidthFor = (rate: number) => 1 + 6 * Math.sqrt((rate || 0) / maxCampaignRate);

    const edges: ElementDefinition[] = [];
    const addEdge = (campaign: Campaign, source?: string, target?: string, suffix?: string) => {
      if (!source || !target || source === target) { return; }
      if (!nodeIds.has(source) || !nodeIds.has(target)) { return; }
      const rate = campaign.rate || 0;
      edges.push({
        group: 'edges',
        data: {
          id: `${campaign.id}-${suffix}`,
          source,
          target,
          campaignId: campaign.id,
          label: campaign.name || '',
          width: edgeWidthFor(rate),
          rateLabel: rate > 0 ? formatRate(rate) : ''
        }
      });
    };
    for (const campaign of activeCampaigns) {
      addEdge(campaign, campaign.creatorId, campaign.accountId, 'ca');
      // When the same company is both account and brand, the creator→account
      // edge above already covers it — don't draw a duplicate edge.
      if (campaign.brandId !== campaign.accountId) {
        addEdge(campaign, campaign.creatorId, campaign.brandId, 'cb');
        addEdge(campaign, campaign.accountId, campaign.brandId, 'ab');
      }
    }

    const connectedIds = new Set<string>();
    for (const edge of edges) {
      connectedIds.add(edge.data['source'] as string);
      connectedIds.add(edge.data['target'] as string);
    }

    this.connectedElements = [...nodes.filter(n => connectedIds.has(n.data.id as string)), ...edges];
    this.unconnectedElements = nodes.filter(n => !connectedIds.has(n.data.id as string));
    this.edgeCount = edges.length;
    this.nodeCount = this.connectedElements.length - edges.length;
  }

  private initGraph(): void {
    this.zone.runOutsideAngular(() => {
      const elements = this.showUnconnected
        ? [...this.connectedElements, ...this.unconnectedElements]
        : this.connectedElements;
      this.cy = cytoscape({
        container: this.graphContainer.nativeElement,
        elements,
        wheelSensitivity: 2,
        style: [
          {
            selector: 'node',
            style: {
              'background-color': 'data(color)',
              width: 'data(size)',
              height: 'data(size)',
              label: 'data(displayLabel)',
              color: '#fff',
              'text-outline-color': '#1f1f1f',
              'text-outline-width': 2,
              'font-size': 'data(fontSize)',
              'text-valign': 'center',
              'text-halign': 'center',
              'text-wrap': 'wrap',
              'text-max-width': '120px',
              'overlay-opacity': 0
            }
          },
          {
            selector: 'edge',
            style: {
              'curve-style': 'bezier',
              'line-color': '#555',
              width: 'data(width)',
              opacity: 0.7,
              label: 'data(rateLabel)',
              'font-size': 9,
              color: '#bbb',
              'text-outline-color': '#181818',
              'text-outline-width': 2,
              'text-rotation': 'autorotate'
            }
          },
          {
            selector: 'node:selected',
            style: {
              'border-width': 3,
              'border-color': '#ffaa33'
            }
          },
          {
            selector: 'edge.highlighted',
            style: {
              'line-color': '#ffaa33',
              color: '#ffaa33',
              opacity: 1
            }
          },
          {
            selector: '.faded',
            style: {
              opacity: 0.12,
              'text-opacity': 0.1
            }
          }
        ]
      });

      this.cy.on('tap', 'node', (event) => {
        const node = event.target as NodeSingular;
        this.focusNode(node);
      });

      this.cy.on('tap', (event) => {
        if (event.target === this.cy) {
          this.clearSelection();
        }
      });

      // Restore the previous layout (positions + viewport) when returning to
      // the page, so the graph appears instantly with no layout run.
      const saved = savedGraphState;
      const canRestore = !!saved && this.cy.nodes().toArray().every(n => !!saved.positions[n.id()]);
      if (canRestore) {
        this.cy.nodes().forEach(n => { n.position(saved!.positions[n.id()]); });
        this.cy.zoom(saved!.zoom);
        this.cy.pan(saved!.pan);
        this.firstLayoutDone = true;
      } else {
        this.runLayout({ fit: true });
      }
    });
  }

  private runLayout(options: { fixedNode?: NodeSingular; fit: boolean }): void {
    if (!this.cy) { return; }
    const layoutOptions: any = {
      name: 'fcose',
      quality: 'default',
      animate: true,
      animationDuration: 600,
      randomize: !this.firstLayoutDone,
      fit: options.fit,
      padding: 60,
      nodeRepulsion: 6000,
      idealEdgeLength: 120
    };
    if (options.fixedNode) {
      layoutOptions.fixedNodeConstraint = [{
        nodeId: options.fixedNode.id(),
        position: { ...options.fixedNode.position() }
      }];
    }
    this.firstLayoutDone = true;
    this.cy.layout(layoutOptions).run();
  }

  // ── Rate scoping ───────────────────────────────────────────────

  /**
   * Rewrite every node's money label. With a creator scope set, each node
   * shows only the campaigns that creator worked (so a brand hub reads as
   * "what this creator earned here", not its all-creator total); with no
   * scope, every node shows its full total again.
   */
  private applyRateScope(creatorId: string | null): void {
    if (!this.cy) { return; }
    this.rateScopeCreatorId = creatorId;
    this.cy.batch(() => {
      this.cy!.nodes().forEach(n => {
        const name = n.data('label') as string;
        let total: number;
        if (creatorId && n.id() !== creatorId) {
          const rows = (n.data('campaigns') as NodeCampaignRow[]) ?? [];
          total = rows.reduce((sum, r) => sum + (r.creatorId === creatorId ? r.rate : 0), 0);
        } else {
          total = (n.data('totalRate') as number) ?? 0;
        }
        n.data('displayLabel', total > 0 ? `${name}\n${formatRate(total)}` : name);
      });
    });
  }

  // ── Selection / focus ──────────────────────────────────────────

  private focusNode(node: NodeSingular): void {
    if (!this.cy) { return; }

    this.applyRateScope(node.data('kind') === 'creator' ? node.id() : null);

    this.cy.elements().removeClass('faded');
    this.cy.edges().removeClass('highlighted');
    this.cy.nodes().unselect();
    node.select();

    const neighborhood = node.closedNeighborhood();
    this.cy.elements().not(neighborhood).addClass('faded');
    neighborhood.edges().addClass('highlighted');

    // Arrange the connected nodes in an even ring around the selected node
    // (which stays where it is) and fit the viewport to that cluster. The
    // rest of the graph is left untouched.
    const center = { ...node.position() };
    const neighbors = neighborhood.nodes().not(node).toArray() as NodeSingular[];

    if (neighbors.length > 0) {
      const avgNeighborSize = neighbors.reduce((sum, n) => sum + (n.data('size') as number), 0) / neighbors.length;
      const angleOf = (n: NodeSingular) => Math.atan2(n.position().y - center.y, n.position().x - center.x);

      // Group neighbors by kind so agencies, brands, and creators each get
      // their own contiguous wedge of the circle instead of being mixed.
      const byKind = new Map<NodeKind, NodeSingular[]>();
      for (const n of neighbors) {
        const kind = n.data('kind') as NodeKind;
        const list = byKind.get(kind) ?? [];
        list.push(n);
        byKind.set(kind, list);
      }

      // Circular mean of each group's current angles, so every wedge is
      // aimed at where its members mostly already are (minimal travel).
      const groups = Array.from(byKind.entries()).map(([kind, members]) => {
        let sx = 0, sy = 0;
        for (const n of members) {
          const a = angleOf(n);
          sx += Math.cos(a);
          sy += Math.sin(a);
        }
        return { kind, members, meanAngle: Math.atan2(sy, sx) };
      });
      groups.sort((a, b) => a.meanAngle - b.meanAngle);

      // Lay the wedges out consecutively, each sized by member count, with
      // the whole circle rotated so the first wedge is centered on its mean.
      const spacing = 16;
      const baseRadius = (node.data('size') as number) / 2 + avgNeighborSize / 2 + 70;
      let cursor = groups[0].meanAngle
        - Math.PI * (groups[0].members.length / neighbors.length);
      const ringGap = avgNeighborSize + 45;
      for (const group of groups) {
        const span = 2 * Math.PI * (group.members.length / neighbors.length);
        // Every member gets its own unique angle spread evenly across the
        // wedge — no two edges from the center ever overlap — while the
        // radius zigzags across however many rows are needed so nodes at
        // adjacent angles sit staggered instead of crowding one ring.
        const sorted = [...group.members].sort((a, b) => angleOf(a) - angleOf(b));
        const anglePerNode = span / sorted.length;
        const rows = Math.max(1, Math.ceil((avgNeighborSize + spacing) / (baseRadius * anglePerNode)));
        sorted.forEach((n, i) => {
          const theta = cursor + anglePerNode * (i + 0.5);
          const radius = baseRadius + (i % rows) * ringGap;
          n.animate(
            { position: { x: center.x + radius * Math.cos(theta), y: center.y + radius * Math.sin(theta) } },
            { duration: 500, easing: 'ease-in-out' }
          );
        });
        cursor += span;
      }
    }

    // After the re-arrange animation lands (500ms), pause for 500ms,
    // then zoom in on the selected node and its neighbors.
    if (this.fitTimer) {
      clearTimeout(this.fitTimer);
    }
    this.fitTimer = setTimeout(() => {
      this.fitTimer = undefined;
      this.cy?.animate({
        fit: { eles: neighborhood, padding: 40 },
        duration: 500,
        easing: 'ease-in-out'
      });
    }, 1000);

    const data = node.data();
    this.zone.run(() => {
      this.searchError = '';
      this.selectedNode = {
        id: data.id,
        label: data.label,
        kind: data.kind,
        kindLabel: data.kindLabel,
        color: data.color,
        totalRate: data.totalRate,
        campaigns: data.campaigns
      };
    });
  }

  clearSelection(): void {
    if (this.fitTimer) {
      clearTimeout(this.fitTimer);
      this.fitTimer = undefined;
    }
    if (this.cy) {
      this.applyRateScope(null);
      this.cy.elements().removeClass('faded');
      this.cy.edges().removeClass('highlighted');
      this.cy.nodes().unselect();
    }
    this.zone.run(() => {
      this.selectedNode = null;
    });
  }

  // ── Toolbar actions ────────────────────────────────────────────

  search(): void {
    if (!this.cy) { return; }
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) { return; }
    const match = this.cy.nodes().filter(n => (n.data('label') as string).toLowerCase().includes(term));
    if (match.length === 0) {
      this.searchError = `No node matching "${this.searchTerm}"`;
      return;
    }
    this.searchError = '';
    this.zone.runOutsideAngular(() => this.focusNode(match[0]));
  }

  fit(): void {
    if (!this.cy) { return; }
    // With a node selected, fit just that node and its neighborhood;
    // otherwise fit the whole graph.
    const eles = this.selectedNode
      ? this.cy.getElementById(this.selectedNode.id).closedNeighborhood()
      : this.cy.elements();
    this.cy.animate({ fit: { eles, padding: 60 }, duration: 400 });
  }

  relayout(): void {
    if (!this.cy) { return; }
    this.zone.runOutsideAngular(() => {
      this.firstLayoutDone = false;
      this.runLayout({ fit: true });
    });
  }

  toggleUnconnected(): void {
    if (!this.cy) { return; }
    this.zone.runOutsideAngular(() => {
      if (this.showUnconnected) {
        // Place the unconnected nodes on a ring around the existing graph so
        // nothing that is already positioned gets moved.
        const added = this.cy!.add(this.unconnectedElements);
        // Newly added nodes carry their unscoped label — re-apply the scope.
        this.applyRateScope(this.rateScopeCreatorId);
        const rest = this.cy!.elements().not(added);
        const bb = rest.length ? rest.boundingBox() : { x1: 0, y1: 0, x2: 0, y2: 0, w: 0, h: 0 };
        const centerX = (bb.x1 + bb.x2) / 2;
        const centerY = (bb.y1 + bb.y2) / 2;
        const radius = Math.max(bb.w, bb.h) / 2 + 200;
        added.forEach((node, i) => {
          const angle = (2 * Math.PI * i) / added.length;
          node.position({
            x: centerX + radius * Math.cos(angle),
            y: centerY + radius * Math.sin(angle)
          });
        });
        this.cy!.animate({ fit: { eles: this.cy!.elements(), padding: 60 }, duration: 400 });
      } else {
        for (const el of this.unconnectedElements) {
          this.cy!.getElementById(el.data.id as string).remove();
        }
        if (this.selectedNode && !this.cy!.getElementById(this.selectedNode.id).length) {
          this.clearSelection();
        }
      }
    });
  }
}
